// Story 8.2 (ADO-2011), Slice 1 — the header cart trigger wiring every full-SiteHeader
// screen shares (through `useHeaderChrome`): the line count from the basket store (the
// badge counts distinct lines; the panel header carries the case total), the cart value (`useCartValue`, the lines summed at their
// locked prices), the open/closed state of the side panel, and the panel element the header
// renders through its `cartPanel` slot. The design-system SiteHeader never reads the
// basket itself; this hook is the app-side seam.
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { closeOpenModals } from '../design-system';
import { useUpsellModalStore } from '../upsell/useUpsellModalStore';
import { useBasketStore } from './useBasketStore';
import { CartSidePanel } from './CartSidePanel';
import { useCartLineCount } from './useCartCaseCount';
import { useCartValue } from './useCartValue';
import type { MoneyValue } from '../shared/money';

export interface CartPanelTrigger {
  /** The cart's distinct lines. Null while the basket has not loaded or its read failed
   *  (shown as unknown). */
  cartCount: number | null;
  /** Null while the basket has not loaded, its read failed, or a line has no locked
   *  price yet (shown as unknown). */
  cartValue: MoneyValue | null;
  onCartClick: () => void;
  cartExpanded: boolean;
  cartPanelId: string;
  cartPanel: ReactNode;
}

export function useCartPanel(): CartPanelTrigger {
  const cartCount = useCartLineCount();
  const cartValue = useCartValue();
  const [open, setOpen] = useState(false);
  const cartPanelId = useId();
  const onCartClick = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);

  // Story 8.3 (ADO-2012), Task 13.4 (card 10, decided with the user): a change refused because
  // the order had gone, made anywhere but the cart page, opens the panel, where the Order sent
  // banner sits. A modal over the page (the upsell, the PDP's add confirmation, or any other open
  // `Modal`) is closed first, so two dialogs never hold focus at once. On the cart page the banner is on the page.
  // Only a new notice opens it: a page whose header mounts while one is still held (it is
  // dropped for leaving the last page just after), or a move off the cart page, does not.
  // ADO-3257 (story D-6): nor does a notice a read made (no product): it is not the Shopper's
  // action, so the banner waits in the panel.
  const { pathname } = useLocation();
  const orderSent = useBasketStore((s) => s.orderSent);
  const noticeSeen = useRef(orderSent);
  useEffect(() => {
    if (orderSent === noticeSeen.current) return;
    noticeSeen.current = orderSent;
    if (orderSent === null || orderSent.skuCode === null || pathname === '/cart') return;
    const upsell = useUpsellModalStore.getState();
    upsell.dismissPending();
    upsell.close();
    // Any other dialog open over the page (the PDP image zoom, a confirmation) closes too.
    closeOpenModals();
    setOpen(true);
  }, [orderSent, pathname]);

  return {
    cartCount,
    cartValue,
    onCartClick,
    cartExpanded: open,
    cartPanelId,
    cartPanel: <CartSidePanel id={cartPanelId} open={open} onClose={onClose} />,
  };
}
